// Enums matching the Smithy model
export enum PrincipalStatus {
  SUBMITTED = "SUBMITTED",
  RUNNING = "RUNNING",
  AVAILABLE = "AVAILABLE",
  FAILED = "FAILED",
  CANCELED = "CANCELED",
}

export enum QueryStatus {
  SUBMITTED = "SUBMITTED",
  RUNNING = "RUNNING",
  AVAILABLE = "AVAILABLE",
  FAILED = "FAILED",
}

export enum ExportStatus {
  PENDING = "PENDING",
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export enum ExportFormat {
  CSV = "CSV",
  TSV = "TSV",
  XLSX = "XLSX",
}

export enum ReportStatus {
  SUBMITTED = "SUBMITTED",
  RUNNING = "RUNNING",
  AVAILABLE = "AVAILABLE",
  FAILED = "FAILED",
  CANCELED = "CANCELED",
}

export enum TimeGranularity {
  YEAR = "YEAR",
  MONTH = "MONTH",
  DAY = "DAY",
}

export enum AggregationFunction {
  SUM = "SUM",
  COUNT = "COUNT",
  AVG = "AVG",
  MIN = "MIN",
  MAX = "MAX",
  ATTRIBUTE = "ATTRIBUTE",
}

export enum JurisdictionLevel {
  COUNTY = "COUNTY",
  CITY = "CITY",
  DISTRICT = "DISTRICT",
}

export enum SplitPeriod {
  NONE = "NONE",
  YEAR = "YEAR",
  QUARTER = "QUARTER",
  MONTH = "MONTH",
}

// Shared types
export interface DateRange {
  startDate: string;
  endDate: string;
}

export type FilterMap = Record<string, string[]>;

export interface PrincipalOutput {
  principalId: string;
  teamId: string;
  status: PrincipalStatus;
  datasets: string[];
  createdAt: string;
  updatedAt: string;
}

export interface QueryStatistics {
  rowCount: number;
  bytesScanned: number;
  executionTimeMs: number;
}

export interface TableColumn {
  name: string;
  type: string;
  description?: string;
}

export interface TableMetadata {
  /// Glue database the table lives in. Required by the service model.
  database: string;
  tableName: string;
  columns: TableColumn[];
  rowCount?: number;
  description?: string;
}

export interface TeamColumn {
  name: string;
  type?: string;
}

export interface OutputColumn {
  columnName: string;
  function: AggregationFunction;
}

export interface ExportConfig {
  format: ExportFormat;
  includeHeaders: boolean;
  columns?: string[];
}

export interface ExportSplitConfig {
  splitBy?: SplitPeriod;
  splitDateColumn?: string;
  linesPerFile?: number;
}

export interface ReportDateRangeFilter {
  column: string;
  range: DateRange;
}

export interface LocalJurisdictionRule {
  level: JurisdictionLevel;
  value: string;
}

export interface JurisdictionFilter {
  states?: string[];
  localRules?: LocalJurisdictionRule[];
}

export interface ColumnFilter {
  column: string;
  values: string[];
}

export interface FilterSpec {
  dateRange?: ReportDateRangeFilter;
  entity?: string;
  jurisdictions?: JurisdictionFilter;
  otherFilters?: ColumnFilter[];
}

export interface ExportFile {
  fileName: string;
  period: string;
  rowCount?: number;
  fileSize?: number;
}

export interface ExportAggregation {
  groupByColumns: string[];
  sumColumns: string[];
}

export type ColumnNameMappingMap = Record<string, string>;

// Request types
export interface CreatePrincipalRequest {
  teamId: string;
  datasets: string[];
}

export interface GetPrincipalRequest {
  teamId: string;
  principalId: string;
}

export interface ListTablesRequest {
  teamId: string;
  principalId: string;
  nextToken?: string;
  maxResults?: number;
}

export interface ExecuteQueryRequest {
  teamId: string;
  principalId: string;
  sql: string;
  parameters?: Record<string, string>;
  maxResults?: number;
}

export interface GetQueryResultsRequest {
  teamId: string;
  queryId: string;
  nextToken?: string;
  maxResults?: number;
}

export interface ListQueriesRequest {
  teamId: string;
  principalId: string;
  maxResults?: number;
  nextToken?: string;
  status?: QueryStatus;
}

export interface CreateExportRequest {
  teamId: string;
  principalId: string;
  name: string;
  datasetType: string;
  dateRanges: DateRange[];
  columns: string[];
  exportName?: string;
  format?: ExportFormat;
  templateId?: string;
  filePrefix?: string;
  dateFormat?: string;
  columnNameMapping?: ColumnNameMappingMap;
  aggregation?: ExportAggregation;
  splitConfig?: ExportSplitConfig;
}

export interface GetExportRequest {
  teamId: string;
  exportId: string;
}

export interface ListExportsRequest {
  teamId: string;
  principalId: string;
  nextToken?: string;
}

export interface ListExportFilesRequest {
  teamId: string;
  exportId: string;
  maxResults?: number;
  nextToken?: string;
}

export interface CreateReportRequest {
  teamId: string;
  principalId: string;
  reportPrefix: string;
  timeColumn: string;
  timeGranularity: TimeGranularity;
  columns: OutputColumn[];
  filters?: FilterSpec;
  exportConfig: ReportExportConfig;
}

export interface GetReportRequest {
  teamId: string;
  reportId: string;
}

export interface ListReportsRequest {
  teamId: string;
  principalId: string;
  nextToken?: string;
}

export interface DeleteReportRequest {
  teamId: string;
  reportId: string;
}

export interface UpdatePrincipalDatasetsRequest {
  teamId: string;
  datasets: string[];
}

export interface GetDownloadUrlRequest {
  teamId: string;
  exportId: string;
  fileName: string;
}

export interface UpdatePrincipalRequest {
  teamId: string;
  principalId: string;
  datasetName?: string;
  description?: string;
  parentId?: string;
  gaiaContextId?: string;
  gaiaContextType?: string;
}

// Response types
export interface CreatePrincipalResponse {
  principal: PrincipalOutput;
}

export interface GetPrincipalResponse {
  principal: PrincipalOutput;
}

export interface ListTablesResponse {
  tables: TableMetadata[];
  nextToken?: string;
}

export interface ExecuteQueryResponse {
  queryId: string;
  status: QueryStatus;
}

export interface GetQueryResultsResponse {
  queryId: string;
  status: QueryStatus;
  columns?: string[];
  rows?: string[][];
  statistics?: QueryStatistics;
  nextToken?: string;
  /**
   * Populated only when status is FAILED. Actionable SQL errors (unknown table,
   * bad column, syntax) are surfaced as-is; system failures are replaced with a
   * generic message by the service.
   */
  errorMessage?: string;
}

export interface QuerySummary {
  queryId: string;
  sql: string;
  status: QueryStatus;
  createdAt: string;
  statistics?: QueryStatistics;
  queryType?: string;
  createdBy?: string;
  resultLocation?: string;
  /** Populated only when status is FAILED. */
  errorMessage?: string;
}

export interface ListQueriesResponse {
  queries: QuerySummary[];
  nextToken?: string;
}

export interface ExportSummary {
  exportId: string;
  principalId: string;
  name: string;
  datasetType: string;
  status: ExportStatus;
  format: ExportFormat;
  createdAt: string;
  createdBy: string;
  exportName?: string;
  database?: string;
  countryCode?: string;
  columns?: string[];
  dateFilter?: ExportDateFilter;
  templateId?: string;
  filePrefix?: string;
  dateFormat?: string;
  columnNameMapping?: ColumnNameMappingMap;
  aggregation?: ExportAggregation;
  files?: ExportFile[];
  errorMessage?: string;
  completedAt?: string;
}

export interface CreateExportResponse {
  exportId: string;
  status: ExportStatus;
  createdAt: string;
  createdBy: string;
}

export interface GetExportResponse {
  exportId: string;
  principalId: string;
  name: string;
  datasetType: string;
  status: ExportStatus;
  format: ExportFormat;
  dateRanges: DateRange[];
  columns: string[];
  createdAt: string;
  createdBy: string;
  completedAt?: string;
  fileCount?: number;
  totalBytes?: number;
  totalRows?: number;
  /**
   * @deprecated No longer populated by the service -- an export's file list is
   * unbounded. Use useListExportFilesQuery instead.
   */
  files?: ExportFile[];
  errorMessage?: string;
}

export interface ListExportsResponse {
  exports: ExportSummary[];
  nextToken?: string;
}

export interface ListExportFilesResponse {
  files: ExportFile[];
  /** Total files in the export, independent of the current page. */
  fileCount?: number;
  nextToken?: string;
}

export interface ReportExportConfig {
  datePeriod: TimeGranularity;
  dateField: string;
  format?: ExportFormat;
  dateFormat?: string;
  splitByLineCount?: number;
}

export interface ReportOutput {
  glueDatabase?: string;
  glueTable?: string;
  s3Path?: string;
}

export interface ReportSummary {
  reportId: string;
  reportPrefix: string;
  status: ReportStatus;
  recordCount?: number;
  createdAt: string;
  createdBy?: string;
  completedAt?: string;
  errorMessage?: string;
}

export interface CreateReportResponse {
  reportId: string;
  status: ReportStatus;
  createdAt: string;
}

export interface GetReportResponse {
  reportId: string;
  principalId: string;
  reportPrefix: string;
  status: ReportStatus;
  timeColumn: string;
  timeGranularity: TimeGranularity;
  columns: OutputColumn[];
  outputColumnNames?: string[];
  filters?: FilterSpec;
  exportConfig?: ReportExportConfig;
  recordCount?: number;
  exportId?: string;
  output?: ReportOutput;
  createdAt: string;
  createdBy?: string;
  completedAt?: string;
  errorMessage?: string;
}

export interface ListReportsResponse {
  reports: ReportSummary[];
  nextToken?: string;
}

export interface DeleteReportResponse {
  reportId: string;
  status: ReportStatus;
}

export interface UpdatePrincipalDatasetsResponse {
  principal: PrincipalOutput;
}

export interface GetDownloadUrlResponse {
  downloadUrl: string;
  fileName: string;
  expiresInSeconds?: number;
}

export interface UpdatePrincipalResponse {
  principalId: string;
  updatedAt: string;
}

export interface ExportDateFilter {
  finalizedDateRange?: DateRange;
  lockPeriods?: string[];
}

// Data & Insights List types
export enum DataWorkflowStatus {
  SUBMITTED = "SUBMITTED",
  RUNNING = "RUNNING",
  AVAILABLE = "AVAILABLE",
  FAILED = "FAILED",
  CANCELED = "CANCELED",
}

export interface PrincipalSummary {
  principalId: string;
  datasetName: string;
  datasetType?: string;
  status: DataWorkflowStatus;
  recordCount: number | null;
  countryCode: string;
  gaiaContextId?: string;
  filters?: Record<string, string[]>;
  dateRange?: { startDate: string; endDate: string };
  taskName?: string;
  createdBy: string;
  createdAt: string;
}

export interface ListPrincipalsRequest {
  teamId: string;
  nextToken?: string;
  maxResults?: number;
}

export interface ListPrincipalsResponse {
  principals: PrincipalSummary[];
  nextToken?: string;
}

export interface DeletePrincipalRequest {
  teamId: string;
  principalId: string;
}

export interface DeletePrincipalResponse {
  success: boolean;
}

// Team Config types (for TAR Data Extraction wizard)
export interface TeamConfigColumn {
  name: string;
  type?: string;
}

export interface GetTeamConfigRequest {
  teamId: string;
}

export interface GetTeamConfigResponse {
  teamId: string;
  version: number;
  columns: TeamConfigColumn[];
  taxTypes?: string[];
  states?: string[];
}

export interface CreatePrincipalDatasetRequest {
  teamId: string;
  parentId: string;
  gaiaContextId: string;
  gaiaContextType: string;
  countryCode: string;
  datasetName: string;
  dateRange: DateRange;
  columns: string[];
  filters: Record<string, string[]>;
  teamConfigVersion: number;
}

export interface CreatePrincipalDatasetResponse {
  principalId: string;
}

// Dataset Detail types
export interface GetPrincipalDatasetRequest {
  teamId: string;
  principalId: string;
}

export interface GetPrincipalDatasetResponse {
  principalId: string;
  datasetName: string;
  status: DataWorkflowStatus;
  recordCount: number | null;
  countryCode: string;
  dateRange: DateRange;
  columns: string[];
  columnNames: string[];
  filters: Record<string, string[]>;
  createdBy: string;
  createdAt: string;
  completedAt?: string;
  gaiaContextId?: string;
  teamConfigVersion: number;
  output?: Record<string, unknown>;
  previewQueryId?: string;
}

export enum TemplateType {
  EXPORT = "EXPORT",
  REPORT = "REPORT",
}

export interface ExportTemplateFields {
  columns: string[];
  format?: ExportFormat;
  dateFormat?: string;
  columnNameMapping?: ColumnNameMappingMap;
  aggregation?: ExportAggregation;
  splitConfig?: ExportSplitConfig;
}

export interface ReportTemplateFields {
  columns: OutputColumn[];
  timeColumn: string;
  timeGranularity: TimeGranularity;
  exportConfig: ReportExportConfig;
  filters?: FilterSpec;
}

/** Exactly one member is set, matching the request's templateType. */
export interface TemplateFields {
  exportFields?: ExportTemplateFields;
  reportFields?: ReportTemplateFields;
}

export interface CreateTemplateRequest {
  teamId: string;
  templateType: TemplateType;
  templateName: string;
  description?: string;
  templateFields: TemplateFields;
}

export interface CreateTemplateResponse {
  templateId: string;
  templateType: TemplateType;
  createdAt: string;
  createdBy: string;
  version: number;
}

export interface GetTemplateRequest {
  templateId: string;
  /** Required query parameter. The service rejects the request without it. */
  teamId: string;
}

/** ListTemplates omits templateFields, so browsing a team does not pull every config. */
export interface TemplateSummary {
  templateId: string;
  teamId: string;
  templateType: TemplateType;
  templateName: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  version: number;
  description?: string;
}

export interface UpdateTemplateRequest {
  templateId: string;
  /** Required query parameter. The service rejects the request without it. */
  teamId: string;
  /** The version last read. A mismatch is rejected with ConflictException (409). */
  version: number;
  templateName?: string;
  description?: string;
  templateFields?: TemplateFields;
}

export interface UpdateTemplateResponse {
  templateId: string;
  updatedBy: string;
  updatedAt: string;
  version: number;
}

export interface DeleteTemplateRequest {
  templateId: string;
  /** Required query parameter. The service rejects the request without it. */
  teamId: string;
}

export interface DeleteTemplateResponse {
  templateId: string;
  /** True on every success, including a repeat delete of an already-deleted template. */
  deleted: boolean;
  updatedBy: string;
  updatedAt: string;
}

export interface ListTemplatesRequest {
  teamId: string;
  /** Omitted returns both types. */
  templateType?: TemplateType;
  nextToken?: string;
}

export interface ListTemplatesResponse {
  templates: TemplateSummary[];
  nextToken?: string;
}

export interface GetTemplateResponse {
  templateId: string;
  teamId: string;
  templateType: TemplateType;
  templateName: string;
  templateFields: TemplateFields;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  /** Pass to UpdateTemplate, which rejects an edit made against a stale value. */
  version: number;
  description?: string;
}
